import "./styles.css";

import { AitisalSdk } from "@aitisal/sdk";
import { Loader, MantineProvider } from "@mantine/core";
import { LoadScript } from "@react-google-maps/api";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import { Amplify } from "aws-amplify";
import ReactDOM from "react-dom/client";
import { Toaster } from "sonner";
import { registerSW } from "virtual:pwa-register";
import axios from "./apis/index";
import { routeTree } from "./routeTree.gen";

const queryClient = new QueryClient();

// Expose QueryClient globally so non-React modules (e.g., MQTT) can invalidate queries
(window as any).__queryClient = queryClient;

const AmplifyConfig = JSON.parse(import.meta.env.VITE_PUBLIC_AMPLIFY_CONFIG!);

Amplify.configure(AmplifyConfig);
AitisalSdk.configure(axios);

// Set up a Router instance
export const router = createRouter({
  routeTree,
  context: {
    queryClient,
  },
  defaultPreload: "intent",
  // Since we're using React Query, we don't want loader calls to ever be stale
  // This will ensure that the loader is always called when the route is preloaded or visited
  defaultPreloadStaleTime: 0,
  defaultViewTransition: true,
  scrollRestoration: true,
});

// Register things for typesafety
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

const rootElement = document.getElementById("app")!;

if (!rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement);

  root.render(
    <MantineProvider>
      <LoadScript
        googleMapsApiKey={import.meta.env.VITE_PUBLIC_GOOGLE_MAPS_API_KEY!}
        libraries={["places", "drawing"]}
        language="en"
        loadingElement={
          <div className="absolute flex justify-center items-center inset-0 bg-white">
            <Loader color="black" />
          </div>
        }
      >
        <QueryClientProvider client={queryClient}>
          <RouterProvider router={router} />
          <Toaster position="top-center" richColors />
        </QueryClientProvider>
      </LoadScript>
    </MantineProvider>,
  );
}

registerSW({
  onOfflineReady() {
    console.log("App ready for offline use");
  },
});
